<template>
  <v-card>
    <template>
      <v-card-title class="i-dialog-header-div">
        <span class="g-bold g-heading-m">Invite User</span>
      </v-card-title>

      <v-card-text
        class="mt-6 mb-4"
        :class="{ 'i-dialog-content-div-large': $vuetifyDisplay.mdAndUp }"
      >
        <v-form v-model="form" @submit.prevent>
          <p class="g-bold g-heading-s g-text-color-black">User Details</p>

          <v-alert
            dense
            outlined
            text
            border="left"
            class="mb-2"
            elevation="1"
            type="info"
          >
            <span class="g-bold">Note: Email Address is required to Login</span>
          </v-alert>

          <v-row class="mt-3">
            <v-col class="py-0" cols="12" md="6">
              <v-text-field
                outlined
                required
                background-color="#F0F0F0"
                dense
                label="Email Address"
                :error-messages="emailErrorMessage"
                :loading="checkingEmail"
                :rules="rulesEmail"
                :value="invitedUserEmail"
                @input="(email) => checkEmailExistsDebounce(email)"
              ></v-text-field>
            </v-col>
            <v-col class="py-0" cols="12" md="6">
              <v-text-field
                outlined
                required
                background-color="#F0F0F0"
                dense
                label="Confirm Email Address"
                v-model="invitedUserEmailConfirmation"
                :rules="[emailConfirmation]"
              ></v-text-field>
            </v-col>

            <v-col class="py-0" cols="12" md="6">
              <v-text-field
                outlined
                required
                background-color="#F0F0F0"
                dense
                label="* First Name (required)"
                v-model="invitedUserFirstName"
                :rules="rulesFirstName"
              ></v-text-field>
            </v-col>
            <v-col class="py-0" cols="12" md="6">
              <v-text-field
                outlined
                required
                background-color="#F0F0F0"
                dense
                label="* Last Name (required)"
                v-model="invitedUserLastName"
                :rules="rulesLastName"
              ></v-text-field>
            </v-col>

            <v-col class="py-0" cols="12" md="6">
              <v-text-field
                outlined
                required
                background-color="#F0F0F0"
                dense
                label="* Mobile or Landline (required)"
                hint="Add Area Code if Landline"
                v-model="invitedUserMobile"
                :rules="rulesPhone"
              ></v-text-field>
            </v-col>
            <v-col class="py-0" cols="12" md="6">
              <v-select
                attach
                background-color="#F0F0F0"
                dense
                :items="businessDepartments"
                label="* Department (Required)"
                outlined
                required
                :rules="[(v) => !!v || 'Department required']"
                v-model="invitedUserDepartment"
              ></v-select>
            </v-col>
          </v-row>

          <v-divider class="mx-0 mt-1 mb-6" horizontal></v-divider>

          <p class="g-bold g-heading-s g-text-color-black">User Role</p>

          <v-row class="mt-4">
            <v-col class="py-0" cols="12">
              <v-select
                attach
                background-color="#F0F0F0"
                dense
                :items="userRoles"
                label="* User Role (Required)"
                outlined
                required
                :rules="[(v) => !!v || 'Role required']"
                v-model="invitedUserRole"
              >
                <template v-slot:item="{ item, attrs, on }">
                  <v-list-item v-bind="attrs" v-on="on">
                    <v-list-item-content>
                      <v-row no-gutters align="center">
                        <span class="g-bold">{{ item.text }}</span>
                        <v-spacer></v-spacer>
                        <div
                          class="px-2 pb-1 g-bg-color-grey g-border-radius-s"
                        >
                          <span class="g-text-s">{{ item.tooltipText }}</span>
                        </div>
                      </v-row>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-select>
            </v-col>
          </v-row>

          <p class="mb-0 pb-0">
            The responsibility for activities that occur by Owner, Admin or
            Standard users within the portal is the responsibility of the
            account owner & registered business.
          </p>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="i-dialog-actions-div justify-end">
        <v-btn rounded text class="i-btn-text-black" @click="handleCancel">
          <strong>Cancel</strong>
        </v-btn>

        <v-btn
          rounded
          class="i-btn-block-secondary mr-4"
          @click="handleConfirm"
          :disabled="!form"
          :loading="loading"
        >
          <strong>Invite</strong>
        </v-btn>
      </v-card-actions>
    </template>
  </v-card>
</template>

<script>
import { mapActions } from 'vuex'
import { addInvitation, checkInvitationExists } from '@/services/invitation'
import { checkEmailExists } from '@/services/profile'
import BusinessDepartments from '@/utils/BusinessDepartments'
import FieldValidators from '@/utils/FieldValidators'

const DEBOUNCE_TIMER = 1000

const USER_ROLES = [
  {
    value: 'ADMIN',
    text: 'Admin',
    tooltipText: 'Access to ALL areas (including My Account functions)'
  },
  {
    value: 'STANDARD',
    text: 'Standard',
    tooltipText: 'Restricted access to Manage Details or Multiple Account Users'
  }
]

export default {
  data: () => ({
    businessDepartments: BusinessDepartments,
    checkingEmail: false,
    emailErrorMessage: null,
    form: false,
    loading: false,
    invitedUserDepartment: null,
    invitedUserEmail: null,
    invitedUserEmailConfirmation: null,
    invitedUserFirstName: null,
    invitedUserLastName: null,
    invitedUserMobile: null,
    invitedUserRole: null,
    userRoles: USER_ROLES,
    ...FieldValidators
  }),

  computed: {
    emailConfirmation() {
      return (
        this.invitedUserEmail === this.invitedUserEmailConfirmation ||
        'Emails must match!'
      )
    },

    isValidEmail() {
      return !!this.invitedUserEmail && /.+@.+/.test(this.invitedUserEmail)
    }
  },

  methods: {
    ...mapActions('notifications', ['addNotification']),

    checkEmailExistsDebounce(email) {
      this.invitedUserEmail = email

      // Only validate in backend once has passed frontend validations
      if (this.isValidEmail) {
        clearTimeout(this.debounceEmail)
        this.debounceEmail = setTimeout(async () => {
          this.checkingEmail = true
          this.emailErrorMessage = 'Validating Email'
          try {
            let exists = await checkEmailExists({ email })
            if (exists) {
              this.emailErrorMessage = 'Email already registered'
            } else {
              exists = await checkInvitationExists(email)
              if (exists) {
                this.emailErrorMessage = 'Email already invited'
              } else {
                this.emailErrorMessage = null
              }
            }
          } catch (err) {
            console.log('Error', err.response)
            // Validation APIs have request limiters
            const errMessage =
              err.response.status === 429
                ? 'We detected too many Email field validation attempts. Please wait a few minutes before trying again.'
                : 'Email validation not completed, try again...'

            this.addNotification({
              color: 'error',
              text: errMessage,
              timeout: -1
            })
          } finally {
            this.checkingEmail = false
          }
        }, DEBOUNCE_TIMER)
      }
    },

    handleCancel() {
      this.invitedUserEmail = null
      this.$emit('cancel')
    },

    handleConfirm() {
      this.loading = true

      const payload = {
        invitedDepartment: this.invitedUserDepartment,
        invitedEmail: this.invitedUserEmail,
        invitedFirstName: this.invitedUserFirstName,
        invitedLastName: this.invitedUserLastName,
        invitedMobile: this.invitedUserMobile,
        invitedRole: this.invitedUserRole
      }

      addInvitation(payload)
        .then(() => {
          this.addNotification({
            color: 'secondary',
            text: 'User invitation sent!'
          })
          this.invitedUserEmail = null
          this.$emit('confirm')
        })
        .catch((err) => {
          let errorMessage = null
          switch (err.response?.data?.code || 'UNKNOWN_ERROR') {
            case 'EMAIL_EXISTS':
              errorMessage =
                'This user email is already registered on our system...'
              break

            default:
              errorMessage = 'User invitation could not be sent...'
          }
          this.addNotification({
            color: 'error',
            text: errorMessage
          })
        })
        .finally(() => {
          this.loading = false
        })
    }
  }
}
</script>
